/**
 * domainHelpers — backward-compatible accessors for the defaultView / fieldType split.
 *
 * Old shape: domain.defaultInputMethod = 'list' | 'individual' | 'points' | 'number'
 * New shape: domain.defaultView = 'list' | 'search'
 *            domain.fieldType  = 'multiChoice' | 'number' | 'binary' | 'text' | 'star'
 */

export function getDomainView(domain) {
  if (!domain) return 'search';
  if (domain.defaultView) return domain.defaultView;
  return domain.defaultInputMethod === 'list' ? 'list' : 'search';
}

/**
 * Historical schema members stay on the effective Domain so old records can
 * still be interpreted. They are not, however, valid inputs for new records
 * or new rules. Keep this predicate at the shared UI boundary so every
 * consumer applies the same `active`/`retired` semantics.
 */
export function isActiveDomainSchemaItem(item) {
  return Boolean(item) && item.active !== false && item.retired !== true;
}

export function getDomainFieldType(domain) {
  if (!domain) return 'multiChoice';
  if (domain.fieldType) return domain.fieldType;
  if (domain.defaultInputMethod === 'number') return 'number';
  // Canonical domains (primary + followUp) carry NO top-level fieldType — by
  // design (see domainRegistry: forcing one would mislabel a number-primary
  // preset). Their input type lives on the primary field, so derive it here.
  // This is what makes Academics (primary.type 'number') ride number mode.
  if (isActiveDomainSchemaItem(domain.primary) && domain.primary?.type) {
    return domain.primary.type;
  }
  return 'multiChoice'; // 'list', 'individual', 'points' all used multiChoice
}

export function isNumberFieldType(domain) {
  return getDomainFieldType(domain) === 'number';
}

/**
 * Return the event-type choices Rules should persist for a Domain.
 *
 * A canonical Domain stores its event identity in the declared primary field,
 * so those option values are authoritative. The separate types collection is
 * retained only for older, fieldType-based Domains that have no primary schema.
 */
export function getDomainEventTypeOptions(domain, legacyTypes = []) {
  const hasCanonicalSchema = Boolean(domain?.primary);
  const canonicalOptions = isActiveDomainSchemaItem(domain?.primary)
    && domain.primary.type === 'multiChoice'
    ? (domain.primary.options || [])
      .filter((option) => isActiveDomainSchemaItem(option) && option?.value != null)
      .map((option) => ({
        value: String(option.value),
        label: option.label || String(option.value),
      }))
    : [];

  if (canonicalOptions.length > 0) return canonicalOptions;
  // A canonical schema with no active choices must remain unavailable. Falling
  // through to legacy type documents could resurrect a retired stable value.
  if (hasCanonicalSchema) return [];

  return (legacyTypes || [])
    .filter(isActiveDomainSchemaItem)
    .map((type) => ({
      value: String(type.value || type.id),
      label: type.label || type.name || String(type.value || type.id),
    }));
}

const NORMALIZED_RULE_FIELD_TYPES = Object.freeze({
  binary: 'boolean',
  boolean: 'boolean',
  multiChoice: 'multiChoice',
  number: 'number',
  star: 'number',
  text: 'text',
  string: 'text',
});

/**
 * Return the declared, staff-authored fields a governed Domain rule may read.
 *
 * Runtime context fields (user ids, timestamps and storage references) are not
 * author-selectable predicates. `star` is retained as a numeric compatibility
 * type for existing Domains, but the creation UI does not advertise it.
 */
export function getDomainRuleFieldOptions(domain) {
  const fields = [domain?.primary, ...(domain?.followUp || [])]
    .filter(isActiveDomainSchemaItem)
    .map((field, index) => {
      const type = NORMALIZED_RULE_FIELD_TYPES[field.type];
      if (!type) return null;
      return {
        id: String(field.id || (index === 0 ? 'primary' : `field-${index + 1}`)),
        label: field.label || (index === 0 ? domain?.label || 'Event' : `Field ${index + 1}`),
        type,
        options: type === 'multiChoice'
          ? (field.options || [])
            .filter((option) => isActiveDomainSchemaItem(option) && option?.value != null)
            .map((option) => ({
              value: String(option.value),
              label: option.label || String(option.value),
            }))
          : [],
      };
    })
    .filter(Boolean);

  if (fields.length > 0) return fields;

  // `primary`/`followUp` is a canonical schema, even if every member is now
  // retired. Do not manufacture a fresh `primary` rule target in that case.
  if (domain?.primary || Array.isArray(domain?.followUp)) return [];

  const fallbackType = NORMALIZED_RULE_FIELD_TYPES[getDomainFieldType(domain)] || 'multiChoice';
  return [{
    id: 'primary',
    label: domain?.terminology?.type?.singular || domain?.label || 'Event',
    type: fallbackType,
    options: [],
  }];
}
